import { Link } from "react-router-dom";
import { BarChart3, Boxes, ShieldCheck, Truck, ArrowRight } from "lucide-react";
import cherrfyLogo from "../assets/images/cherrfy-logo.png";

const features = [
  {
    title: "Operations Dashboard",
    description: "Monitor key KPIs, orders, and system health in one place.",
    icon: BarChart3,
  },
  {
    title: "Product & Catalog Tools",
    description: "Create, import, and manage products with faster workflows.",
    icon: Boxes,
  },
  {
    title: "Secure Admin Controls",
    description: "Role-based access and audit-friendly settings for teams.",
    icon: ShieldCheck,
  },
  {
    title: "Shipping & Integrations",
    description: "Track fulfilment and courier integrations with visibility.",
    icon: Truck,
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-white">
      <header className="mx-auto max-w-6xl px-4 pt-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={cherrfyLogo}
              alt="Cherrfy"
              className="h-10 w-auto"
              draggable={false}
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-slate-900">Internal</div>
              <div className="text-xs text-slate-500">Operations console</div>
            </div>
          </div>

          <Link
            to="/administrator/dashboard"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
          >
            Enter Console <ArrowRight size={16} />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-16 pt-12">
        <section className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Run operations with clarity.
            </h1>
            <p className="mt-4 text-base leading-7 text-slate-600">
              A unified internal workspace for managing products, orders, partners, and system
              settings — built for speed and reliability.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/administrator/dashboard"
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-rose-700"
              >
                Open Dashboard <ArrowRight size={16} />
              </Link>
              <Link
                to="/administrator/products"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
              >
                Manage Products
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-100 bg-white p-4">
                <div className="text-xs text-slate-500">Environment</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">Internal</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-4">
                <div className="text-xs text-slate-500">Frontend</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">React</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-white p-4">
                <div className="text-xs text-slate-500">API</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">api.cherrfy.com</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-rose-200/60 via-white to-slate-100 blur-2xl" />
            <div className="relative rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                {features.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.title}
                      className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4"
                    >
                      <div className="flex items-center gap-2">
                        <span className="grid h-9 w-9 place-items-center rounded-xl bg-white text-rose-700 shadow-sm">
                          <Icon size={18} />
                        </span>
                        <div className="text-sm font-semibold text-slate-900">{f.title}</div>
                      </div>
                      <div className="mt-2 text-sm text-slate-600">{f.description}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-4">
                <div className="text-xs font-medium text-slate-500">Quick access</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/administrator/orders"
                    className="rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    Orders
                  </Link>
                  <Link
                    to="/administrator/users"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Users
                  </Link>
                  <Link
                    to="/administrator/analytics"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Analytics
                  </Link>
                  <Link
                    to="/administrator/settings"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                  >
                    Settings
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="mt-16 border-t border-slate-100 pt-8 text-sm text-slate-500">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>© {new Date().getFullYear()} Cherrfy</div>
            <div className="text-xs">For internal operational use.</div>
          </div>
        </footer>
      </main>
    </div>
  );
}
